import React, { useEffect, useState } from "react";
import { marketApi } from "@/lib/api";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TID } from "@/lib/testIds";

// Hoisted constants — recharts prop objects were re-created on every render.
const CHART_MARGIN = { top: 5, right: 5, left: -20, bottom: 0 };
const AXIS_TICK = { fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" };
const Y_DOMAIN = ["auto", "auto"];
const TOOLTIP_CONTENT_STYLE = {
  background: "#12141D",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  fontFamily: "JetBrains Mono",
  fontSize: 12,
};
const TOOLTIP_LABEL_STYLE = { color: "#94A3B8" };
const tooltipFormatter = (v) => [`$${v}`, "Close"];

export default function PriceChart({ symbol, onSymbolChange, watchlist }) {
  const [data, setData] = useState([]);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [d, q] = await Promise.all([marketApi.intraday(symbol), marketApi.quote(symbol)]);
        if (!mounted) return;
        setData((d.points || []).map((p) => ({ t: p.t.slice(-8, -3), close: p.close, high: p.high, low: p.low })));
        setQuote(q);
      } catch (err) {
        console.debug("[PriceChart] fetch failed", err);
      }
    })();
    return () => { mounted = false; };
  }, [symbol]);

  const up = (quote?.change ?? 0) >= 0;
  const stroke = up ? "#10B981" : "#EF4444";
  const fill = up ? "rgba(16,185,129,0.18)" : "rgba(239,68,68,0.18)";

  return (
    <div className="aq-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Chart</div>
            <div className="font-display text-lg font-bold">{symbol}</div>
          </div>
          {quote && (
            <div className="pl-4 border-l border-white/10">
              <div className="font-mono text-2xl font-bold">${quote.price}</div>
              <div className={`font-mono text-xs ${up ? "text-emerald-300" : "text-rose-300"}`}>
                {up ? "+" : ""}{quote.change} ({up ? "+" : ""}{quote.change_percent}%)
              </div>
            </div>
          )}
        </div>
        <Select value={symbol} onValueChange={onSymbolChange}>
          <SelectTrigger data-testid={TID.chartSymbolSelect} className="w-32 h-9 bg-[#0D0E14] border-white/10 font-mono text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#12141D] border-white/10">
            {(watchlist || []).map((s) => (
              <SelectItem key={s} value={s} className="font-mono">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={CHART_MARGIN}>
            <defs>
              <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity={0.5} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="t" tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={40} />
            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} domain={Y_DOMAIN} />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={{ color: stroke }}
              formatter={tooltipFormatter}
            />
            <Area type="monotone" dataKey="close" stroke={stroke} strokeWidth={2} fill="url(#grad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
